import { Image, Space } from 'antd';
import React, { useEffect, useState, useRef } from 'react';
import { BaseButton } from '../common/BaseButton/BaseButton';
import { BaseCol } from '../common/BaseCol/BaseCol';
import { BaseRow } from '../common/BaseRow/BaseRow';
import { BaseButtonsForm } from '../common/forms/BaseButtonsForm/BaseButtonsForm';
import { DeleteOutlined, FilePdfOutlined, FileTextOutlined, FileWordOutlined } from '@ant-design/icons';
import { TransferDirection } from 'antd/lib/transfer';
import { FileInfo, uploadFile, removeFile } from '@app/api/assets.api';
import { BaseList } from '../common/BaseList/BaseList';
import { BaseTable } from '../common/BaseTable/BaseTable';
import { ColumnsType } from 'antd/lib/table';
import { notificationController } from '@app/controllers/notificationController';
import { array } from 'prop-types';

interface AssetsProps {
  type: 'images' | 'files';
  code: string;
  targetFiles: (value: any) => void;
  previouslySelectedFiles: FileInfo[];
  editable: boolean;
}

enum Action {
  CREATE = '1',
  NO_CHANGE = '2',
  DELETE = '3',
}

export const Assets: React.FC<AssetsProps> = ({
  type,
  code,
  targetFiles,
  previouslySelectedFiles,
  editable = false,
}) => {
  const [uploadform] = BaseButtonsForm.useForm();
  const [isformFieldsChanged, setformFieldsChanged] = useState(false);
  const [fileList, setFileList] = useState<FileList | null>(null);
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorFiles, setErrorFiles] = useState<{ file: string; error: string }[]>([]);  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFiles(previouslySelectedFiles);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFileList(event.target.files);
      setErrorFiles([]);
    }
  };

    const handleFileUpload = async () => {
      setLoading(true);
      if (!fileList) return;

      const errors: { file: string; error: string }[] = [];
      const newFiles = [...files];
      const remainingFiles = Array.from(fileList);

      await Promise.all(
        Array.from(fileList).map(file => {
          console.log(file);
          const formData = new FormData();
          formData.append('code', code);
          formData.append('type', type);
          formData.append('file', file);
    
          return uploadFile(formData)
            .then((response) => {
              console.log(response?.file);
              newFiles.push(response?.file);
              const index = remainingFiles.indexOf(file);
              if (index > -1) {
                remainingFiles.splice(index, 1);
              }
            })
            .catch((error) => {
              errors.push({ file: file.name, error: error.message });
              notificationController.error({
                  message:'Error:',
                  description:`${error.message}`
                });
              });
        })
      );
    
      setFiles(newFiles);
      targetFiles(newFiles);
      setErrorFiles(errors);

      if (remainingFiles.length > 0) {
        console.log("has remining files");

        const dataTransfer = new DataTransfer();
        remainingFiles.forEach(file => dataTransfer.items.add(file));
        setFileList(dataTransfer.files);
        if (fileInputRef.current) {
          fileInputRef.current.files = dataTransfer.files;
        }
      } else {
        if (fileInputRef.current) {
          setFileList(null);
          uploadform.setFieldsValue({file:''});
          notificationController.success({
            message:'Success:',
            description: 'Selected file(s) uploaded successfully!'
          });
      }
                
      }
      setLoading(false);
  };

  const getFileIcon = (extension: string) => {
    switch (extension) {
      case 'pdf': {
        return <FilePdfOutlined style={{ color: 'var(--error-color)', fontSize: '2rem' }} />;
      }
      case 'doc':
      case 'docx': {
        return <FileWordOutlined style={{ color: 'var(--primary-color)', fontSize: '2rem' }} />;
      }
      default: {
        return <FileTextOutlined style={{ color: 'var(--warning-color)', fontSize: '2rem' }} />;
      }
    }
  };

  const handleDeleteFile = (file: FileInfo, index: number) => {
    if (file.action == '1') {

      const formData = new FormData();
      formData.append('code', code);
      formData.append('type', type);
      formData.append('file', file.name);

      removeFile(formData)
      .then((response) => {
        notificationController.success({
          message:'Success:',
          description:`${response.message}`
        });
      })
      .catch((error) => {
        notificationController.error({
            message:'Error:',
            description:`${error.message}`
          });
        });
      const updateItems = files?.filter((_, i) => i != index);
      setFiles([...updateItems]);
      targetFiles([...updateItems]);
    } else if (file.action == '2') {
      const deleteFile: FileInfo = { ...file, action: Action.DELETE };
      const updateItems = files?.filter((e, i) => i != index && e.id != file.id);
      setFiles([...updateItems, deleteFile]);
      targetFiles([...updateItems, deleteFile]);
    }
  };

  const columns: ColumnsType<FileInfo> = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (_, record) =>
        type === 'files' ? (
          getFileIcon(record.extension)
        ) : (
          <Image
            preview={false}
            width={100}
            height={100}
            src={record.url ?? 'error'}
           // fallback="data:image/png;base64,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"
          />
        ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record, index) => (
        <Space size="middle">
          <BaseButton danger type="text" icon={<DeleteOutlined />} onClick={() => handleDeleteFile(record, index)} />
        </Space>
      ),
    },
  ];
  console.log(errorFiles,errorFiles.length);

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol lg={24}>
          {editable && (
            <BaseButtonsForm
              name="form"
              form={uploadform}
              isFieldsChanged={isformFieldsChanged}
              setFieldsChanged={setformFieldsChanged}
            >
              <BaseRow gutter={[16, 16]}>
                <BaseCol>
                  <BaseButton onClick={handleFileUpload} loading={loading}>
                    Upload File
                  </BaseButton>
                  {errorFiles.length > 0 && (
                    <span style={{ color: 'red', fontSize: '0.8em' }}>{errorFiles.length} file(s) remaining</span>
                  )}
                </BaseCol>
                <BaseCol>
                  <BaseButtonsForm.Item name="file">
                    <input
                      type="file"
                      accept={type === 'files' ? '.pdf,.doc,.docx,.txt' : 'image/*'} multiple
                      onChange={handleFileChange}
                      ref={fileInputRef}
                    />
                  </BaseButtonsForm.Item>
                </BaseCol>
              </BaseRow>
              <BaseRow gutter={[16, 16]}>
                <BaseCol>
                 
                {errorFiles.length > 0 && (
                  <div style={{ color: 'gray', fontSize: '0.9em' }}>
                    <span style={{ paddingTop: '1em', display: 'block' }}>Failed to upload:</span>
                    <ul style={{ color: 'red', fontSize: '0.9em', paddingLeft: '1em' }}>
                      {errorFiles.map((error, index) => (
                        <li key={index}>
                          {error.file} - {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                </BaseCol>
              </BaseRow>
            </BaseButtonsForm>
          )}

          <BaseRow gutter={[16, 16]}>
            <BaseCol lg={24}>
              <BaseTable
                bordered
                dataSource={files?.filter((e) => e.action !== Action.DELETE)}
                columns={!editable ? columns.slice(0, 2) : columns}
                pagination={false}
              />
            </BaseCol>
          </BaseRow>
        </BaseCol>
      </BaseRow>
    </>
  );
};
